<template>
  <c-box>
    <!-- Remove site dialog -->
    <dialog-modal
      :show-dialog="showRemoveSiteDialog"
      @close="onRemoveSite(false)"
      content-ref="remove-site-dialog"
    >
      <template v-slot:title>
        {{ $t("productReturn.common.dialogs.removeSiteTitle") }}
      </template>
      <template v-slot:content>
        <c-text>{{ $t("productReturn.common.dialogs.removeSiteBody") }}</c-text>
      </template>
      <template v-slot:actions>
        <c-button
          variation="solid"
          @click="onRemoveSite(true)"
          background="brand.beige"
          borderColor="brand.beige"
          mr="md"
        >
          {{ $t("common.yesButton") }}
        </c-button>
        <c-button @click="onRemoveSite(false)">
          {{ $t("common.noButton") }}
        </c-button>
      </template>
    </dialog-modal>

    <!-- Add product dialog -->
    <RMACRProductModal
      v-if="showProductDialog"
      :productToEdit="productToEdit"
      :showProductDialog="showProductDialog"
      :proFormaRequired="proFormaRequired"
      @hide="showProductDialog = false"
      @addReturnProduct="onAddProduct"
      @editReturnProduct="onUpdateProduct"
    />

    <form novalidate @submit.prevent="submit">
      <c-box
        box-shadow="base"
        border-radius="base"
        bg="white"
        :px="{ base: 'md', md: '4xl' }"
        :pt="{ base: 'xl', md: '2.5rem' }"
        :pb="{ base: '2xl', md: '3rem' }"
        :class="getClasses()"
      >
        <c-flex align-items="center" :mb="{ base: 'lg', md: '3xl' }">
          <c-heading as="h3" font-weight="bold" font-size="xl">
            {{ $t("productReturnV2.siteEditor.title") }} {{ index }}
          </c-heading>
          <c-button
            v-if="count > 1"
            @click="onShowRemoveSiteDialog()"
            border-color="transparent"
            pl="sm"
            pr="sm"
            ml="auto"
          >
            <c-icon name="trashcan" size="1.4rem" />
          </c-button>
        </c-flex>
        <c-box>
          <c-form-control is-required :is-invalid="inputIsInvalid('form.siteDetails.name')">
            <c-box
              display="flex"
              :flex-direction="{ base: 'column', sm: 'row' }"
              align-items="baseline"
            >
              <c-form-label :min-width="{ xl: '21rem' }" :for="'site-name-' + index">
                {{ $t("productReturn.siteEditor.name") }}
              </c-form-label>
              <c-box flex="1" width="100%">
                <c-input
                  :id="'site-name-' + index"
                  size="lg"
                  v-model.trim="form.siteDetails.name"
                  @keyup="onFieldChange('form.siteDetails.name')"
                  @blur="onFieldBlur('form.siteDetails.name')"
                  :data-cy="`site-name-${index}`"
                />
                <c-box
                  v-if="isErrorField('form.siteDetails.name')"
                  color="brand.red"
                  font-size="sm"
                  my="xs"
                  data-cy="site-name-error"
                >
                  {{
                    $t("common.error.fieldInvalid", {
                      field: $t("productReturn.siteEditor.name"),
                    })
                  }}
                </c-box>
              </c-box>
            </c-box>
          </c-form-control>
        </c-box>

        <c-box>
          <c-form-control :is-invalid="inputIsInvalid('form.siteDetails.reference')">
            <c-box
              display="flex"
              :flex-direction="{ base: 'column', sm: 'row' }"
              align-items="baseline"
            >
              <c-form-label :min-width="{ xl: '21rem' }" :for="'reference-' + index">
                {{ $t("productReturn.siteEditor.reference") }}
              </c-form-label>
              <c-box flex="1" width="100%">
                <c-input
                  :id="'reference-' + index"
                  size="lg"
                  v-model.trim="form.siteDetails.reference"
                  @keyup="onFieldChange('form.siteDetails.reference')"
                  @blur="onFieldBlur('form.siteDetails.reference')"
                  :data-cy="`site-reference-${index}`"
                />
                <c-box
                  v-if="isErrorField('form.siteDetails.reference')"
                  color="brand.red"
                  font-size="sm"
                  my="xs"
                  data-cy="site-reference-error"
                >
                  {{
                    $t("common.error.fieldInvalid", {
                      field: $t("productReturn.siteEditor.reference"),
                    })
                  }}
                </c-box>
              </c-box>
            </c-box>
          </c-form-control>
        </c-box>

        <c-flex :flex-direction="{ base: 'column', sm: 'row' }" align-items="baseline">
          <c-text as="h3" font-size="md" :min-width="{ xl: '21rem' }" :mr="{ base: 'sm', xl: '0' }">
            {{ $t("productReturnV2.siteEditor.productsToReturn") }}:
          </c-text>

          <c-box flex="1" width="100%">
            <c-box v-for="(product, i) in form.products" :key="'RMA_PC_' + i" mb="lg">
              <c-box
                border="1px solid"
                py="0.25rem"
                pl="md"
                pr="0.25rem"
                border-radius="input"
                d="inline-flex"
                align-items="center"
                justify-items="stretch"
                width="100%"
                :class="{
                  'is-invalid': singleProductIsInvalid(i) || containsDuplicateSerial(product),
                }"
                v-chakra="
                  (theme) => ({
                    borderColor:
                      singleProductIsInvalid(i) || containsDuplicateSerial(product)
                        ? 'brand.redLight'
                        : 'brand.blue',
                    backgroundColor:
                      singleProductIsInvalid(i) || containsDuplicateSerial(product)
                        ? 'brand.grayLight'
                        : 'brand.blueLightest',
                  })
                "
              >
                <c-text as="span" flex="1">
                  {{ product.count }} x {{ product.sku }} {{ product.name }}
                </c-text>

                <c-box
                  :d="{ base: 'grid', sm: 'inline-flex' }"
                  :gap="{ base: '1rem', sm: '0' }"
                  :grid-template-areas="`
                    'edit edit'
                    'info trash'
                  `"
                  :align-items="{ sm: 'center' }"
                  :my="{ base: 'xs', sm: '0' }"
                >
                  <c-tooltip
                    hasArrow
                    :label="generateTooltip(product)"
                    p="sm"
                    border-radius="input"
                  >
                    <c-text
                      as="span"
                      grid-area="info"
                      :mr="{ sm: 'sm' }"
                      tabindex="0"
                      :d="{ base: 'flex', sm: 'inline' }"
                      align-items="center"
                      justify-content="center"
                    >
                      <c-icon name="info" size="1.4rem" color="brand.purple" />
                    </c-text>
                  </c-tooltip>
                  <c-button
                    @click="editProduct(i)"
                    text-transform="uppercase"
                    height="2rem"
                    mr="0.25rem"
                    grid-area="edit"
                  >
                    {{ $t("productReturnV2.common.edit") }}
                  </c-button>
                  <c-button
                    @click="deleteProduct(i)"
                    border-color="transparent"
                    pl="xs"
                    pr="xs"
                    grid-area="trash"
                  >
                    <c-icon name="trashcan" size="1.4rem" />
                  </c-button>
                </c-box>
              </c-box>
              <c-box v-if="singleProductIsInvalid(i)" color="brand.red" font-size="sm" my="xs">
                {{ $t("productReturnV2.common.error.invalidProductDetails") }}
              </c-box>
            </c-box>
            <c-button
              border-radius="input"
              width="100%"
              justify-content="flex-start"
              pl="md"
              pr="md"
              py="xl"
              white-space="initial"
              text-align="left"
              :disabled="!form.siteDetails.name"
              @click="
                productToEdit = undefined;
                showProductDialog = true;
              "
            >
              <c-icon name="plus" size="1.25rem" mr="md" />
              <c-text
                as="span"
                v-if="!form.products.length"
                :data-cy="`site-${index}-add-first-product`"
              >
                {{ $t("productReturnV2.siteEditor.addProduct") }}
              </c-text>
              <c-text as="span" v-else :data-cy="`site-${index}-add-another-product`">
                {{ $t("productReturnV2.siteEditor.addAnotherProduct") }}
              </c-text>
            </c-button>
            <c-box
              v-if="isErrorField('form.products') && form.products.length === 0"
              color="brand.red"
              font-size="sm"
              my="xs"
              data-cy="site-products-empty-error"
            >
              {{
                $t("common.error.fieldRequired", {
                  field: $t("productReturnV2.siteEditor.productsToReturn"),
                })
              }}
            </c-box>
          </c-box>
        </c-flex>
      </c-box>
    </form>
  </c-box>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { required, minLength, maxLength } from "vuelidate/lib/validators";
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import ToggleBox from "@/components/ToggleBox.vue";
import Button from "@/design-system/elements/Button.vue";
import Icon from "@/design-system/elements/Icon.vue";
import Modal from "@/components/Modal.vue";
import {
  CTooltip,
  CText,
  CButton,
  CBox,
  CIcon,
  CInput,
  CFlex,
  CFormLabel,
  CFormControl,
  CHeading,
} from "@chakra-ui/vue";
import RMACRProductModal from "./RMACRProductModal.vue";
import { IMaintenanceReturnProductRequest, IMaintenanceReturnSitesRequest } from "@iloq/shared";
import {
  productValidationWithoutProductCategory,
  productValidationWithProductCategory,
} from "./RMACRShared";
import { inputMixin } from "./RMAMixins";

export default defineComponent({
  mixins: [inputMixin, FormValidationMixin],
  components: {
    ToggleBox,
    Button,
    Icon,
    "dialog-modal": Modal,
    CTooltip,
    CText,
    CButton,
    RMACRProductModal,
    CBox,
    CIcon,
    CInput,
    CFlex,
    CFormLabel,
    CFormControl,
    CHeading,
  },
  props: {
    index: Number,
    count: Number,
    proFormaRequired: Boolean,
    duplicateSerials: Array as PropType<string[]>,
  },
  data() {
    return {
      formErrors: [] as any[],
      actions: {} as Record<string, unknown>,
      form: {
        $hasErrors: false,
        siteDetails: {
          name: "",
          reference: "",
        },
        products: [],
      } as IMaintenanceReturnSitesRequest,
      showRemoveSiteDialog: false,
      showProductDialog: false,
      productToEdit: undefined as
        | { index: number; product: IMaintenanceReturnProductRequest }
        | undefined,
    };
  },
  validations() {
    return {
      form: {
        siteDetails: {
          name: { required, maxLength: maxLength(100) },
          reference: { maxLength: maxLength(30) },
        },
        products: {
          required,
          minLength: minLength(1),
          $each: {
            ...(this.proFormaRequired
              ? productValidationWithProductCategory
              : productValidationWithoutProductCategory),
          },
        },
      },
    };
  },
  methods: {
    singleProductIsInvalid(index: number): boolean {
      return !!this.$v?.form?.products?.$each?.[index]?.$invalid;
    },
    containsDuplicateSerial(product: IMaintenanceReturnProductRequest): boolean {
      return !!product.serials.find((serial) => this.duplicateSerials.includes(serial));
    },
    getClasses(): string {
      let classes = "product_editor";
      if (this.hasErrors()) {
        classes += " has-errors";
      }
      return classes;
    },
    onShowRemoveSiteDialog(): void {
      this.showRemoveSiteDialog = true;
    },
    onRemoveSite(success: boolean): void {
      this.showRemoveSiteDialog = false;
      if (success) {
        this.removeSite();
      }
    },
    generateTooltip({
      replacementNeeded,
      repairPermission,
      discardPermission,
    }: IMaintenanceReturnProductRequest) {
      const translationKeys = {
        replacementNeeded: "productReturnV2.siteEditor.replacementNeeded",
        repairPermission: "productReturnV2.siteEditor.repairPermission",
        discardPermission: "productReturnV2.siteEditor.discardPermission",
        requireQuote: "productReturnV2.siteEditor.requireQuote",
      };

      const permissions = {
        replacementNeeded,
        repairPermission,
        discardPermission,
      };

      const somePermissions = Object.values(permissions).some((val) => val === true);

      if (!somePermissions) {
        return this.$t(translationKeys["requireQuote"]);
      }

      return Object.keys(permissions)
        .filter((key) => permissions[key] === true)
        .map((key) => `${this.$t(translationKeys[key])}`)
        .join(", ");
    },
    removeSite(): void {
      this.$emit("onRemove", this.index);
    },
    onRemove(index: number): void {
      this.form.products.splice(index - 1, 1);
      this.formErrors.splice(index - 1, 1);
      this.checkErrors();
    },
    editProduct(index: number): void {
      this.productToEdit = { index, product: this.form.products[index] };
      this.showProductDialog = true;
    },
    deleteProduct(index: number): void {
      this.form.products.splice(index, 1);
    },
    onAddProduct(product: any): void {
      this.showProductDialog = false;
      this.form.products.push(product);
      this.productToEdit = undefined;
    },
    onUpdateProduct(product: any, index: number): void {
      const products = [...this.form.products];
      products[index] = product;
      this.form.products = products;

      this.showProductDialog = false;
      this.productToEdit = undefined;

      const productModel = this.$v.form.products.$model;
      this.$v.form.products.$model = null;
      this.$v.form.products.$model = productModel;
    },
    hasErrors(): boolean {
      return this.formErrors.length > 0;
    },
    onErrors(index: number, ev: boolean): void {
      this.formErrors[index - 1] = ev;
      this.checkErrors();
    },
    checkErrors(): void {
      if (this.formErrors.indexOf(true) >= 0) {
        this.addFormError("products", null);
      } else {
        this.removeFormError("products");
      }
    },
    submit(): void {
      // Do nothing
    },
  },
  watch: {
    "form.products": function () {
      this.onFieldChange("form.products");
    },
  },
});
</script>
